<script setup lang="ts">
import { GameType, type Game } from '@game/types'
import { vIntersectionObserver } from '@vueuse/components'
import { isEmpty } from 'es-toolkit/compat'
import type { Usp } from '#layers/uk/types'
import { ExperimentCookieName, ExperimentName, ExperimentValue } from '@core/types'

definePageMeta({
    layout: false,
    middleware: [
        'slots-landing-page'
    ],
    layoutBackgroundColor: '#422A0E',
    landingPage: true,
    heroVideo: true,
})

defineOptions({
    prefetch: (to) => {
        useSignupPromotions({ query: to.query, server: true, lazy: false })
    },
})

const title = 'Online Slots | Play Progressive Jackpot Slots at Happy Tiger'
const description = 'Happy Tiger is packed with unique slots, exclusive bonus games & huge progressive jackpots. Sign-up now. T&Cs apply. 18+. Play responsibly.'

const url = useRequestURL()
const links = useLinks()
const experiment = useExperiment({ cookieName: ExperimentCookieName.LANDING_PAGE, experimentName: ExperimentName.LANDING_PAGE })
const { authOpen } = useLandingPageAuth()
const experimentType = computed(() => (experiment.value || ExperimentValue.A) as ExperimentValue)

const { getHead } = useContentSeo({
    metaTitle: title,
    metaDescription: description
})

useHead(getHead())

// unhead v3 dropped `keywords` from useSeoMeta's input, so it ships as a plain meta tag.
useHead({
    meta: [
        { name: 'keywords', content: 'happy tiger, bingo, slots, casino, new bingo, new slots, new casino, slots uk, slots online, happy tiger bingo' }
    ]
})

useSeoMeta({
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogType: 'article',
    ogLocale: 'en_GB',
    ogUrl: `${url.origin}${url.pathname}`,
    ogImageUrl: `${url.origin}/cdn-cgi/image/fit=scale-down,format=webp,quality=75,width=1200,height=630/media/logo_opengraph.jpg`,
    ogImageWidth: 1200,
    ogImageHeight: 630
})

// Games
const rootMargin = '100px'
const page_size = 15

const popularGamesLoaded = ref(false)
const { data: popularGames } = useGames({
    params: { page: 1, page_size },
    filter: {
        game_types: [GameType.SLOTS]
    },
    key: 'slots-popular',
    lazy: true,
    enabled: () => toValue(popularGamesLoaded)
})

function loadPopularGames(entries: IntersectionObserverEntry[]) {
    if (!entries[0]?.isIntersecting || popularGamesLoaded.value) return

    popularGamesLoaded.value = true
}

// Games with jackpots and Game Jackpots: Get game jackpots and setup the websocket listener
const { gamesJackpots } = useGamesJackpots()

const sortedJackpotSlugs = ref<string[]>([])

whenever(
    () => isEmpty(gamesJackpots?.value) ? undefined : gamesJackpots.value,
    (val) => {
        sortedJackpotSlugs.value = Object.keys(val)
            .sort((a, b) => Number.parseFloat(String(val[b]?.superJackpot ?? val[b]?.jackpot)) - Number.parseFloat(String(val[a]?.superJackpot ?? val[a]?.jackpot)))
    },
    { once: true, immediate: true }
)

const jackpotGamesLoaded = ref(false)
const jackpotFilter = computed(() => {
    return {
        slugs: toValue(sortedJackpotSlugs),
        has_jackpot: true
    }
})

const { data: jackpotGames } = useGames({
    params: { page: 1, page_size },
    filter: jackpotFilter,
    lazy: true,
    key: 'slots-jackpot-games',
    enabled: () => !!toValue(sortedJackpotSlugs)?.length && toValue(jackpotGamesLoaded)
})

function loadJackpotGames(entries: IntersectionObserverEntry[]) {
    if (!entries[0]?.isIntersecting || jackpotGamesLoaded.value) return

    jackpotGamesLoaded.value = true
}

const sortedJackpotGames = ref<Game[]>([])

whenever(
    () => jackpotGames?.value?.data?.length ? jackpotGames.value.data : undefined,
    (data) => {
        sortedJackpotGames.value = data.slice().sort((a: Game, b: Game) => {
            const idxA = sortedJackpotSlugs.value.indexOf(a.slug)
            const idxB = sortedJackpotSlugs.value.indexOf(b.slug)

            return idxA - idxB
        })
    },
    { once: true, immediate: true }
)

const fallbackContent = {
    backgroundColor: '#1181c9',
    videoId: '22b2d6aa3f8cf5456f047d27eb568034',
    buttonText: 'Continue',
    trustpilotEnabled: undefined,
    usps: undefined
}

const uspsBeforeGames: Array<Usp> = [
    {
        title: 'Discover unique online slots only available at Happy Tiger Casino',
        description: 'Welcome to Happy Tiger, your destination for the most exclusive progressive online slots in the UK. We provide a unique selection of online slots that can\'t be found anywhere else. Whether you\'re a fan of classic styles or prefer the fun of retro video slots, we have something exclusively for you. Dive into our growing selection of games where each slot has its own progressive jackpot to be won.',
        media: {
            type: 'video',
            src: 'https://media.platego.io/slots_lobby/16-9_Happytiger_showreel_-_Oktober.mp4?v=1',
            poster: '/slots_lobby/16-9_Happytiger_showreel_-_Oktober.jpg?v=2'
        }
    },
    {
        title: 'Progressive jackpot slots',
        description: 'What sets our collection of our online slots apart is not just their exclusivity, but also the fun progressive jackpots all of our games offer. We feature games from reputable developers, all of which are unique to Happy Tiger. Our slots differ from traditional slots by providing a unique gaming experience that you won\'t find anywhere else. Check out our gameplay video to see some of these exciting features in action!',
        media: {
            type: 'image',
            alt: 'Deep blue Casino slot machine',
            src: '/media/allslots/usps/2.png',
            width: '640',
            height: '394'
        }
    },
    {
        title: 'User-Friendly Gaming',
        description: 'At Happy Tiger, we believe in making online gaming accessible, enjoyable, and exclusive. That\'s why we\'ve made our platform user-friendly and our games easy to understand. Whether you\'re a seasoned player or new to slots, you\'ll find our site easy to navigate and our games fun to play.',
        media: {
            type: 'image',
            alt: 'Diamond express mini game',
            src: '/media/allslots/usps/3.png',
            width: '640',
            height: '394'
        }
    },
]

const uspsAfterGames: Array<Usp> = [
    {
        title: 'Start Your Gaming Journey',
        description: 'Are you ready? Dive into the world of exclusive online slots with progressive jackpots at Happy Tiger today and discover your new favourite game. Remember, you won\'t find our titles anywhere else - this is the home of the most exclusive slots in the UK.',
        media: {
            type: 'video',
            src: 'https://media.platego.io/slots_lobby/temple_of_the_sun_showcase_v1%20%281080p%29.mp4?v=1',
            poster: '/slots_lobby/temple_of_the_sun_showcase_v1.jpg?v=2'
        }
    },
    {
        title: 'Experience the Fun',
        description: 'With our exclusive slots, every spin brings fun. Our gameplay videos give you a small taste of the fun, showing you just what you can expect when you play our exclusive slots.',
        media: {
            type: 'image',
            alt: 'Railroad Express mini game',
            src: '/media/allslots/usps/4.png',
            width: '640',
            height: '394'
        }
    },
    {
        title: '<a href="/bingo">Bingo Fun at Happy Tiger</a>',
        description: 'In addition to our slots, we also offer a Bingo game at Happy Tiger. This game, with its straightforward gameplay, is a favourite among players of all skill levels. Whether you\'re following along with the number calls or simply enjoying the game, our Bingo offering adds an extra layer to your Happy Tiger experience. <a href="/bingo">Join the fun - Play Bingo now!</a>',
        media: {
            type: 'image',
            alt: 'Bingo Game Flash',
            src: '/media/allslots/usps/6.png',
            width: '640',
            height: '394'
        }
    },
    {
        title: 'Join Our Casual Casino Gaming Site',
        description: `
            Looking for fun, casual casino games with fast payouts?
            At Happy Tiger, we bring you real money games built for casual players — no pressure, just great entertainment.
            We're creating one of the best online casino experiences in the UK,
            packed with exclusive slots and Super Jackpot games you won't find anywhere else.
            With a strong focus on responsible play and a welcoming player community,
            Happy Tiger keeps it social, safe, and simple.
            Sign up now to explore some of the best real money games online — only at Happy Tiger.
            `,
        media: {
            type: 'image',
            alt: 'Social chat',
            src: '/media/allslots/usps/7.png',
            width: '640',
            height: '394'
        }
    }
]
</script>

<template>
    <div :class="[`landing-page landing-page--${experiment}`, { 'landing-page--auth-open': authOpen }]">
        <UIApp>
            <LandingPageNavBar />

            <main>
                <LandingPageHeaderContainer
                    :type="experimentType"
                    fallback-path="/slots-lobby"
                    :content="fallbackContent"
                    background-color="#1181c9"
                />
                <StickyTerms
                    title="Significant Conditions"
                    is-landing-page
                    reset-margin
                    :sticky-config="{
                        container: '.landing-page',
                        minHeight: 48,
                        offset: 50,
                        backgroundColor: '#1181c9'
                    }"
                />

                <section ref="welcomeSection" class="section welcome">
                    <div class="welcome__description-container">
                        <div v-intersection-observer="[loadPopularGames, { rootMargin }]" class="section__content">
                            <div class="games-list">
                                <GameList
                                    :games="popularGames?.data"
                                    :inline-rows="2"
                                    title="MUST-PLAY ONLINE SLOTS"
                                    category="POPULAR"
                                    :video-enabled="false"
                                    @game-click="game => trackGameOpen('gametile', game)"
                                />
                            </div>
                        </div>
                        <div class="welcome__description">
                            Hi and Welcome to Happy Tiger Casino & Slots! Enjoy a wide selection of exclusive online
                            slots and bingo games at Happy Tiger. Join our friendly community, explore unique games, and
                            start your gaming adventure today. Sign up now!
                        </div>
                    </div>
                    <!-- USPs before games -->
                    <div class="section__content">
                        <LazyVisibleUspsSection :usps="uspsBeforeGames" color="#fff" hydrate-on-visible />
                    </div>

                    <div class="section__content">
                        <div class="games-list">
                            <GameList
                                v-intersection-observer="[loadJackpotGames, { rootMargin }]"
                                :games="sortedJackpotGames"
                                :inline-rows="2"
                                title="EXCLUSIVE ONLINE SLOT GAMES"
                                category="JACKPOT"
                                :video-enabled="false"
                                @game-click="game => trackGameOpen('gametile', game)"
                            />
                        </div>
                    </div>

                    <!-- USPs after games -->
                    <div class="section__content">
                        <LazyVisibleUspsSection :usps="uspsAfterGames" color="#fff" hydrate-on-visible />
                    </div>
                </section>
                <section class="section trustpilot">
                    <!-- <TrustpilotWidget /> -->
                </section>

                <section class="section section--dark join">
                    <div class="section__content">
                        <h1 class="join__title">
                            Join Happy Tiger Bingo
                        </h1>
                        <p class="join__description">
                            & Grab your welcome offer today
                        </p>

                        <UIButton
                            v-bind="links.signup.props()"
                            class="join__button"
                            as="cta"
                            size="xl"
                        >
                            Sign Up Now
                        </UIButton>
                    </div>
                </section>

                <section class="section section--dark join__terms">
                    <div class="section__content">
                        <StickyTerms title="Bonus terms" is-landing-page force-full-terms />
                    </div>
                </section>
            </main>

            <div class="landing-page__footer">
                <LazyVisibleFooterContainer :enable-terms="false" is-landing-page hydrate-on-visible />
            </div>
        </UIApp>
    </div>
</template>

<style scoped lang="scss">
$body-bg-light: #1181c9;
$body-bg-dark: #1181c9;
$yellow: #f6d701;
$content-width: 1024px;
$content-width--restricted: 800px;

.landing-page {
    --theme-background: var(--background, #{$body-bg});
    --body-bg-light: #{$body-bg-light};
    --body-bg-dark: #{$body-bg-dark};
    --sticky-terms-color: #fff;
    --login-button-color: #fff;
    background-color: var(--body-bg-dark);

    .theme-provider {
        --background: var(--body-bg-dark);
        color: #fff;
    }

    &__footer {
        padding-top: 50px;
        background-color: var(--theme-background, $body-bg);
    }

    :deep(.header__description) {
        font-size: 14px;
    }

    :deep(.text--featured) {
        color: #ffff00;
    }

    main {
        .section {
            &__content {
                max-width: $content-width;
                width: 100%;
                margin: auto;
            }

            &--dark {
                background-color: var(--body-bg-dark);
            }
        }

        a {
            color: inherit;
        }

        .welcome {
            display: grid;
            grid-template-areas:
                'description'
                'usps';
            background-color: var(--body-bg-light);
            color: $white;
            margin-top: 30px;

            :deep(.game-section),
            :deep(.skeleton-list__headline) {
                text-align: center;
            }

            &__description-container {
                background-color: var(--body-bg-dark);
            }

            &__description {
                max-width: $content-width--restricted;
                margin: 0 auto;
                padding: 0 1rem 3rem;
                text-align: center;
                font-size: 1.2rem;
                word-wrap: break-word;
                font-weight: 800;

                @include tablet {
                    font-size: 1.375rem;
                }
            }

            .games-list {
                padding: 1rem 1rem 2rem;
            }
        }

        .about {
            padding: 0 1rem 0 1rem;
            margin-top: -10px;
            background-color: var(--body-bg-light);
            color: $white;

            @include tablet {
                padding-top: 4rem;
            }

            &__item {
                max-width: $content-width--restricted;
                margin: 0 auto;
                padding-bottom: 1rem;

                &-title {
                    padding-bottom: 10px;
                    font-weight: 800;
                    font-size: 28px;
                }
            }
        }

        .trustpilot {
            display: flex;
            justify-content: center;
            background-color: var(--body-bg-light);
            color: $white;
            padding-top: 1rem;
            padding-bottom: 1rem;

            @include tablet {
                padding-bottom: 5rem;
            }
        }

        .join {
            padding-top: 2rem;
            padding-bottom: 3rem;
            color: $white;
            text-align: center;

            @include tablet {
                padding-bottom: 4rem;
            }

            .section__content {
                display: grid;
                grid-template-columns: 1fr;
                justify-items: center;
            }

            &__title {
                color: $yellow;
                font-size: 1.6rem;
                font-weight: 800;

                @include mobile {
                    font-size: 1.8rem;
                }

                @include mobile(sm) {
                    font-size: 2rem;
                }

                @include tablet {
                    font-size: 3rem;
                }
            }

            &__description {
                font-size: 1.2rem;
                font-weight: 600;

                @include tablet {
                    font-size: 1.5rem;
                }
            }

            &__button {
                margin-top: 1.5rem;
                width: 90vw;

                @include tablet {
                    width: 414px;
                }
            }

            &__terms {
                padding-bottom: 3rem;

                @include tablet {
                    padding-bottom: 4rem;
                }

                :deep(.offer-terms__title) {
                    margin-bottom: 3px;
                    font-size: 12px;
                }
            }
        }
    }

    .footer {
        max-width: 1024px;
        margin: 0 auto;
        padding: 0 2rem 2rem 2rem;

        @include desktop-xl {
            max-width: $screen-xl;
        }
    }
}
</style>
